/**
 * Where a notification opens — one answer for the bell and the Activity page.
 *
 * Both surfaces render the same events and only one of them could act on any
 * of them: the bell deep-linked GROUP events and sent everything else to the
 * Activity feed, and the Activity feed itself passed no handler at all, so a
 * row reading `tracer “Fresh wallet 0xb0c2…7898 — Across to Base + poisoning —
 * flow” run on demand` was a sentence naming a thing with no way to reach it.
 * With eighteen tracers and twenty watches, that leaves the reader to find by
 * name what the platform had the id for all along (`link_id`, set by every
 * emitter with a subject).
 *
 * The mapping lives here, once, so the two surfaces cannot drift — and it is a
 * pure function of the event so it is testable without a router.
 */
import { eventTarget, type EventBase } from "@authe/fe-commons/events";

/** A TanStack `navigate` descriptor, narrow enough to hand straight over. */
export interface EventRoute {
  to: string;
  params?: Record<string, string>;
  search?: Record<string, string>;
  hash?: string;
}

/**
 * The route a notification opens, or `null` when the platform has no page for
 * its subject — in which case the caller keeps its own fallback rather than
 * inventing a link that 404s.
 */
export function eventRoute(event: EventBase): EventRoute | null {
  const metadata = event.metadata as Record<string, unknown> | undefined;
  if (event.resource === "investigation" && event.link_id) {
    return { to: "/investigations/$id", params: { id: event.link_id },
      ...(metadata?.anchor_kind === "contributions" ? { hash: "contributions" } : {}) };
  }
  const findingId = metadata?.group_id;
  if (typeof findingId === "string" && findingId) return { to: "/groups/$id", params: { id: findingId }, search: { situationScope: "SYSTEM" } };
  const target = eventTarget(event);
  if (!target) return null;
  switch (target.kind) {
    case "group":
      return { to: "/groups/$id", params: { id: target.id } };
    case "tracer":
      return { to: "/tracers/$id", params: { id: target.id } };
    case "watch":
      return { to: "/watches/$id", params: { id: target.id } };
    case "address":
      // The Conduct tab is not the default and this link is not about conduct:
      // an address event is a catalog or activity fact, so it opens the page
      // the reader would have opened themselves.
      return { to: "/addresses/$id", params: { id: target.id } };
    default:
      return null;
  }
}
